import type { ComputedRef, Ref } from 'vue'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import type { Hit } from '@nuxtjs/algolia'
import {
  EventListPageType,
  type StrapiEventListPage,
} from '~/composables/types/strapiEventListPage'
import type { KftApiDefaultResponse } from '~/composables/types/kft-api'
import type { LocalityCategory } from '~/composables/types/locality-category'
import type { Category } from '~/composables/types/event'
import type { Locality } from '~/composables/types/locality'
import {
  eventListPageCategoryUrl,
  eventListPageLocalitiesUrl,
  eventListPageLocalityCategoriesUrl,
  eventListPageTagUrl,
} from '~/components/transactionalPage/requestParams'
import type { SearchEngineEventItem } from '~/composables/types/searchEngine'
import { organizationReference } from '~/composables/schemaOrg/useOrganizationSchema'

export const useTransactionalProductSchema = async ({
  locationSlug,
  tagSlug,
  categorySlug,
  searchHeading,
  pageType,
  eventListPageData,
  hits = ref([]),
  eventCount,
  reviewsData = null,
}: {
  locationSlug: ComputedRef<string> | Ref<string | undefined>
  tagSlug: ComputedRef<string> | Ref<string | undefined>
  searchHeading: ComputedRef<string> | Ref<string | null>
  categorySlug: ComputedRef<string | undefined> | Ref<string | undefined>
  eventListPageData?:
    | ComputedRef<StrapiEventListPage>
    | Ref<StrapiEventListPage>
    | StrapiEventListPage
  pageType: EventListPageType
  hits: ComputedRef<Hit<SearchEngineEventItem>[]>
  eventCount?: ComputedRef<number | undefined>
  reviewsData?: {
    reviews: any[]
    averageRating: string
    reviewCount: number
  } | null
}) => {
  const nuxtApp = useNuxtApp()
  // Before any await: composables lose the Nuxt instance after one.
  const pageUrl = useFullLocalizedUrl(true)(useRoute().path)
  type TransformedLocalityCategory = {
    permalink: string
    title: string
    slug: string
  }
  const localityPromise = useKftApiFetch<KftApiDefaultResponse<Locality>>(
    eventListPageLocalitiesUrl({ locationSlug: locationSlug.value }),
    {
      immediate:
        EventListPageType.CITY === pageType ||
        pageType === EventListPageType.CITY_CATEGORY,
    }
  )

  const tagsPromise = useKftApiFetch<KftApiDefaultResponse<Locality>>(
    eventListPageTagUrl({ tagSlug: tagSlug.value }),
    {
      immediate:
        EventListPageType.GENERAL === pageType ||
        EventListPageType.TEAMEVENT === pageType,
    }
  )

  const localityCategoriesPromise = nuxtApp.runWithContext(() =>
    useKftApiFetch<
      KftApiDefaultResponse<LocalityCategory[]>,
      TransformedLocalityCategory[]
    >(eventListPageLocalityCategoriesUrl(), {
      immediate: EventListPageType.CITY_CATEGORY === pageType,
      transform: (res) => {
        // Transform is useful for eliminating large amounts of unnecessary data
        // that will occupy space inside the document (ssr)
        return (
          res?.data?.map((category) => ({
            title: category.title,
            permalink: category.permalink,
            slug: category.slug,
            avg_review: category.avg_review,
            total_reviews: category.total_reviews,
          })) || ([] as TransformedLocalityCategory[])
        )
      },
    })
  )

  const categoryPromise = nuxtApp.runWithContext(() =>
    useKftApiFetch<KftApiDefaultResponse<Category>>(
      eventListPageCategoryUrl({ categorySlug: categorySlug.value }),
      { immediate: EventListPageType.CATEGORY === pageType }
    )
  )

  const [
    { data: localityResponse },
    { data: localityCategoriesResponse },
    { data: categoryResponse },
    { data: tagResponse },
  ] = await Promise.all([
    localityPromise,
    localityCategoriesPromise,
    categoryPromise,
    tagsPromise,
  ])

  const localityCategoryObj = computed(() => {
    const cityCategorySlug = `${locationSlug.value}-${categorySlug.value}`

    return (
      localityCategoriesResponse.value?.find(
        (x) => x.slug === cityCategorySlug
      ) || null
    )
  })

  const categoryObj = computed(() => {
    return categoryResponse.value?.data
  })

  const tagObj = computed(() => {
    return tagResponse.value?.data
  })

  const computedReviews = computed(() => {
    const map = {
      // TODO check if these values are right and complete the map
      [EventListPageType.CATEGORY]: categoryObj.value,
      [EventListPageType.CITY]: localityResponse.value?.data,
      [EventListPageType.CITY_CATEGORY]: localityCategoryObj.value,
      [EventListPageType.GENERAL]: tagObj.value,
      [EventListPageType.TEAMEVENT]: tagObj.value,
    } as Record<
      string,
      Category | LocalityCategory | Locality | undefined | null
    >

    return {
      averageRating: map[pageType]?.avg_review,
      totalReviews: map[pageType]?.total_reviews,
    }
  })

  // aggregateRating is what earns the review snippet, so the Product is only
  // emitted when both rating numbers exist.
  const shouldAppendTransactionalProductSchema = computed(() => {
    return Boolean(
      computedReviews.value?.totalReviews &&
      computedReviews.value?.averageRating
    )
  })

  dayjs.extend(utc)
  dayjs.extend(timezone)

  // The price range comes from the events listed on the page. offerCount stays
  // the total number of results, as in production.
  const aggregateOffer = computed(() => {
    const offer = getSchemaAggregateOffer(
      hits.value.map((hit) => ({
        low: searchEngineEventGetters.getSchemaMinimumPriceAmount(hit) / 100,
        high: searchEngineEventGetters.fnGetMaximumPriceAmount(hit) / 100,
        currency: searchEngineEventGetters.fnGetMinimumPriceCurrency(hit),
      }))
    )
    return offer && eventCount?.value
      ? { ...offer, offerCount: eventCount.value }
      : offer
  })

  const image = computed(() => {
    const seoImage = strapiEventListPageGetters.getSeoImageUrl(
      toValue(eventListPageData) as StrapiEventListPage
    )
    if (seoImage) return seoImage

    const firstHitImage = hits.value[0]
      ? searchEngineEventGetters.getPrincipalImage(hits.value[0])
      : ''
    return firstHitImage ? toKonfettiWidth(firstHitImage, 1200) : undefined
  })

  // Generate review objects from reviewsData if available
  const reviewObjects = computed(() => {
    if (!reviewsData?.reviews || reviewsData.reviews.length === 0) {
      return []
    }

    return reviewsData.reviews.map((review: any) => ({
      '@type': 'Review',
      reviewRating: {
        '@type': 'Rating',
        ratingValue: review.rating,
        bestRating: 5,
        worstRating: 1,
      },
      author: {
        '@type': 'Person',
        name: review.reviewee_name || review.customer || 'Anonymous',
      },
      reviewBody: review.review_text || review.review,
      datePublished: review.review_date || review.created_at,
    }))
  })

  return {
    shouldAppendTransactionalProductSchema,
    schemaObj: computed(() => {
      return {
        type: 'application/ld+json',
        innerHTML: {
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: searchHeading.value,
          url: pageUrl,
          ...(image.value && { image: image.value }),
          brand: organizationReference,
          ...(aggregateOffer.value && { offers: aggregateOffer.value }),
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: computedReviews.value.averageRating,
            reviewCount: computedReviews.value.totalReviews,
            bestRating: 5,
            worstRating: 1,
          },
          ...(reviewObjects.value.length > 0
            ? { review: reviewObjects.value }
            : {}),
        },
      }
    }),
  }
}
